import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, FileText, Send, Check, Ban, X } from "lucide-react";
import { TAX_CATEGORIES } from "./adminShared";

const STATUS = {
  unpaid: { label: "Obetald", cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  paid: { label: "Betald", cls: "bg-[#10B981]/15 text-[#10B981]" },
  cancelled: { label: "Makulerad", cls: "bg-[#EF4444]/15 text-[#FCA5A5]" },
};
const PAY_METHODS = [["bank_transfer", "Banköverföring"], ["swish", "Swish"], ["card", "Kort"], ["cash", "Kontant"]];
const EMPTY_LINE = { description: "", amount: "", tax_category: "" };
const EMPTY_FORM = { order_number: "", customer_name: "", customer_email: "", customer_reference: "", due_days: "30", notes: "", lines: [{ ...EMPTY_LINE }] };

export default function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [payDialog, setPayDialog] = useState(null);     // invoice
  const [cancelDialog, setCancelDialog] = useState(null); // invoice
  const [cancelReason, setCancelReason] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/admin/invoices"); setInvoices(res.data.invoices || []); }
    catch { toast.error("Kunde inte ladda fakturor"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name, customer_email: form.customer_email,
        customer_reference: form.customer_reference, due_days: parseInt(form.due_days) || 30,
        notes: form.notes,
      };
      if (form.order_number.trim()) payload.order_number = form.order_number.trim();
      else payload.lines = form.lines
        .filter(l => l.description.trim() && parseFloat(l.amount))
        .map(l => ({ description: l.description.trim(), amount: parseFloat(l.amount),
                     tax_category: l.tax_category }));
      await api.post("/admin/invoices", payload);
      toast.success("Faktura skapad");
      setDialog(false); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skapa fakturan"); }
    finally { setSaving(false); }
  };

  const act = async (id, fn, okMsg) => {
    setBusyId(id);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Misslyckades"); }
    finally { setBusyId(null); }
  };

  const fmt = (s) => { try { return new Date(s).toLocaleDateString("sv-SE"); } catch { return ""; } };

  return (
    <div className="max-w-3xl" data-testid="invoices-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Fakturor</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Löpnummerserie, moms redovisad — fakturor makuleras, raderas aldrig.</p>
        </div>
        <Button onClick={() => setDialog(true)} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Ny faktura
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <FileText className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga fakturor ännu. Skapa från ett ordernummer eller med egna rader.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {invoices.map(inv => (
            <div key={inv.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold">
                    {inv.invoice_number} · {inv.customer_name}
                    <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS[inv.status]?.cls}`}>{STATUS[inv.status]?.label}</span>
                    {inv.sent_at && <span className="ml-1.5 text-[10px] text-[#93C5FD]">skickad</span>}
                  </p>
                  <p className="text-[11px] text-[#9A9AA2] mt-0.5">
                    {inv.total?.toFixed(2)} kr ({inv.vat_breakdown?.length
                      ? inv.vat_breakdown.map(b => `moms ${b.rate}%: ${b.vat?.toFixed(2)} kr`).join(" · ")
                      : `varav moms ${inv.vat_amount?.toFixed(2)} kr`}) · förfaller {inv.due_date}
                    {inv.order_number && ` · order #${inv.order_number}`} · {fmt(inv.created_at)}
                  </p>
                  {inv.cancel_reason && <p className="text-[11px] text-[#FCA5A5] mt-1">Makulerad: {inv.cancel_reason}</p>}
                </div>
                {inv.status === "unpaid" && (
                  <div className="flex gap-1.5 shrink-0">
                    {inv.customer_email && (
                      <Button size="sm" variant="outline" disabled={busyId === inv.id}
                        onClick={() => act(inv.id, () => api.post(`/admin/invoices/${inv.id}/send`), "Fakturan skickas via mejl")}
                        className="border-[#3B82F6]/40 text-[#93C5FD] hover:bg-[#3B82F6]/10 h-8 text-xs">
                        <Send className="w-3.5 h-3.5 mr-1" /> Skicka
                      </Button>
                    )}
                    <Button size="sm" disabled={busyId === inv.id} onClick={() => setPayDialog(inv)}
                      className="bg-[#10B981] hover:bg-[#059669] text-white h-8 text-xs">
                      <Check className="w-3.5 h-3.5 mr-1" /> Betald
                    </Button>
                    <Button size="sm" variant="outline" disabled={busyId === inv.id}
                      onClick={() => { setCancelDialog(inv); setCancelReason(""); }}
                      className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs">
                      <Ban className="w-3.5 h-3.5 mr-1" /> Makulera
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Ny faktura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Ordernummer (valfritt — hämtar raderna från ordern)</Label>
              <Input value={form.order_number} onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))}
                placeholder="t.ex. 0531" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kundnamn *</Label>
                <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kundens e-post</Label>
                <Input value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Referens / org.nr</Label>
                <Input value={form.customer_reference} onChange={e => setForm(f => ({ ...f, customer_reference: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Betalvillkor (dagar)</Label>
                <Input type="number" value={form.due_days} onChange={e => setForm(f => ({ ...f, due_days: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
            </div>
            {!form.order_number.trim() && (
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Fakturarader</Label>
                {form.lines.map((l, i) => (
                  <div key={i} className="flex gap-2 mb-2 flex-wrap">
                    <Input value={l.description} placeholder="Beskrivning"
                      onChange={e => setForm(f => { const ls = [...f.lines]; ls[i] = { ...ls[i], description: e.target.value }; return { ...f, lines: ls }; })}
                      className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] flex-1 min-w-[140px]" />
                    <Input type="number" value={l.amount} placeholder="Belopp"
                      onChange={e => setForm(f => { const ls = [...f.lines]; ls[i] = { ...ls[i], amount: e.target.value }; return { ...f, lines: ls }; })}
                      className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-28" />
                    <select value={l.tax_category || ""} title="Momskategori (obligatorisk)"
                      onChange={e => setForm(f => { const ls = [...f.lines]; ls[i] = { ...ls[i], tax_category: e.target.value }; return { ...f, lines: ls }; })}
                      className={`bg-[#0A0A0A] border rounded-md h-10 text-xs px-1.5 w-36 ${l.tax_category ? "border-[#27272A] text-[#C8C8CE]" : "border-[#F59E0B]/60 text-[#F59E0B]"}`}>
                      <option value="">Välj moms *</option>
                      {TAX_CATEGORIES.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                    </select>
                    <button onClick={() => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))}
                      className="text-[#A8ABB2] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }))}
                  className="text-[#D4B483] hover:text-[#E5CFA5] text-xs font-medium">+ Lägg till rad</button>
              </div>
            )}
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anteckning på fakturan</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
            </div>
            <Button onClick={create} disabled={saving || !form.customer_name.trim() ||
                (!form.order_number.trim() && form.lines.some(l => l.description.trim() && parseFloat(l.amount) && !l.tax_category))}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Skapa faktura
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={!!payDialog} onOpenChange={(o) => { if (!o) setPayDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Markera betald — {payDialog?.total?.toFixed(2)} kr</DialogTitle></DialogHeader>
          <p className="text-xs text-[#C0C0C6]">{payDialog?.invoice_number} · {payDialog?.customer_name}. Hur betalades fakturan?</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {PAY_METHODS.map(([id, label]) => (
              <button key={id} onClick={() => { const inv = payDialog; setPayDialog(null);
                  act(inv.id, () => api.post(`/admin/invoices/${inv.id}/mark-paid`, { method: id }), "Fakturan är betald"); }}
                className="py-3 rounded-xl border border-[#27272A] bg-[#0A0A0A] hover:border-[#10B981]/50 hover:bg-[#10B981]/5 text-xs text-[#EDEDED] font-medium transition-all">
                {label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => { if (!o) setCancelDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Makulera {cancelDialog?.invoice_number}?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#C0C0C6]">Fakturan raderas inte — den markeras som makulerad och behålls i serien (bokföringsregler).</p>
          <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2}
            placeholder="Anledning *" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="border-[#3F3F46] text-white hover:bg-[#1A1A1A]" onClick={() => setCancelDialog(null)}>Avbryt</Button>
            <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white" disabled={cancelReason.trim().length < 3}
              onClick={() => { const inv = cancelDialog; setCancelDialog(null);
                act(inv.id, () => api.post(`/admin/invoices/${inv.id}/cancel`, { reason: cancelReason.trim() }), "Fakturan makulerades"); }}>
              Makulera
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
